// DOM-agnostic article scoring core.
//
// Both lib/articleAnalysis.ts (browser) and lib/articleAnalysisServer.ts (Node)
// build a MinimalDoc adapter around their respective HTML parsers and call
// `analyzeFromDoc()` here. Same heuristics, same score, same checklist —
// shared between the dashboard panel and the post-generation QC gate.
//
// Keep this file framework-free: no `import { ... } from "react"`, no
// browser globals (`window`, `DOMParser`), no Node-only APIs (`fs`, `Buffer`).

export type Bucket = "red" | "amber" | "green";

export type Heading = {
    level: 2 | 3;
    text: string;
    slug: string;
};

export type ChecklistItem = {
    key: string;
    label: string;
    passed: boolean;
    severity: "required" | "recommended";
    state?: "passed" | "failed" | "na";
    /**
     * Optional human-readable explanation for the UI tooltip. Surfaces when
     * the user wants to know why a check failed and what to do about it.
     * Kept optional so existing checklist items don't all need to be
     * touched at once.
     */
    tooltip?: string;
};

export type ArticleStats = {
    wordCount: number;
    readingMinutes: number;
    keywords: number;
    images: number;
    internalLinks: number;
    externalLinks: number;
    // Structural counts harvested by lib/site-fingerprint.ts to compute
    // per-site rolling medians. Exposed here so the fingerprint module can
    // reuse a single DOM parse instead of re-walking the HTML, and so any
    // future dashboard that visualises the fingerprint can read them too.
    h2Count: number;
    h3Count: number;
    blockquotes: number;
    paragraphs: number;
    videoEmbeds: number;
};

// Per-site rolling median fingerprint computed from the last N published
// articles. When present (sample size >= 10), the recommended-severity
// checks adapt their thresholds to match the site's own pattern instead
// of the global defaults. Sites with fewer than 10 published articles
// fall back to the static defaults — no cold-start surprise.
export type SiteFingerprint = {
    sampleSize: number;
    wordCount: number;
    h2Count: number;
    h3Count: number;
    internalLinks: number;
    externalLinks: number;
    blockquotes: number;
    paragraphs: number;
    videoEmbedRate: number; // 0-1: fraction of sampled articles with at least one iframe/video
    derivedAt: string; // ISO timestamp
};

export type StatThresholds = {
    wordCount: number;
    images: number;
    internalLinks: number;
    externalLinks: number;
    keywords: number;
};

export const DEFAULT_THRESHOLDS: StatThresholds = {
    wordCount: 800,
    images: 1,
    // Internal links pass at the first inserted link — once the semantic
    // linker has wired even one related sibling, the check goes green.
    // Higher floors silently failed early-corpus sites where the linker
    // could only land 1 candidate above similarity threshold.
    internalLinks: 1,
    // 5+ authoritative external citations passes the score check. The
    // client's editorial target is still 8-10 (peer-reviewed studies,
    // .gov/.edu, reputable publications), but most well-sourced articles
    // hit 5 reliably and dragging the panel red below that floor was
    // misleading. Articles still publish below this — severity is
    // "recommended", not "required" — they just get a yellow flag.
    externalLinks: 5,
    keywords: 1,
};

export type ScoreReport = {
    score: number;
    bucket: Bucket;
    stats: ArticleStats;
    checklist: ChecklistItem[];
    toc: Heading[];
    hasFaq: boolean;
    hasKeyTakeaways: boolean;
};

export type AnalyzeMeta = {
    title: string;
    keyword: string | null;
    metaDescription: string | null;
    featuredImageUrl: string | null;
    siteHost: string | null;
    publishedSiblingCount?: number;
    // Optional. When provided AND sampleSize >= MIN_FINGERPRINT_SAMPLE,
    // adaptive thresholds replace the static defaults for a subset of
    // recommended checks (see deriveFingerprintThresholds). Absent / null
    // / under-sampled fingerprints fall back to defaults silently.
    siteFingerprint?: SiteFingerprint | null;
};

// Minimum sample size before fingerprint thresholds replace the defaults.
// Below this we use defaults — fewer than 10 published articles is too
// noisy to reliably represent the site's pattern.
export const MIN_FINGERPRINT_SAMPLE = 10;

export const EMPTY_REPORT: ScoreReport = {
    score: 0,
    bucket: "red",
    stats: {
        wordCount: 0,
        readingMinutes: 0,
        keywords: 0,
        images: 0,
        internalLinks: 0,
        externalLinks: 0,
        h2Count: 0,
        h3Count: 0,
        blockquotes: 0,
        paragraphs: 0,
        videoEmbeds: 0,
    },
    checklist: [],
    toc: [],
    hasFaq: false,
    hasKeyTakeaways: false,
};

// Minimal interface every wrapper must implement. Only the subset of DOM
// operations analyzeFromDoc() actually uses — keeps the server adapter small.
//
// Convention: `tagName` is UPPERCASE (matches browser DOM behaviour).
// `textContent` is the recursively-concatenated text — never null in our
// adapters; we coerce to "" when missing.
export interface MinimalElement {
    readonly tagName: string;
    readonly textContent: string;
    getAttribute(name: string): string | null;
}

export interface MinimalDoc {
    readonly body: { readonly textContent: string };
    querySelector(selector: string): MinimalElement | null;
    querySelectorAll(selector: string): MinimalElement[];
}

export function slugify(text: string): string {
    return text
        .toLowerCase()
        .trim()
        .replace(/[^\p{Letter}\p{Number}\s-]/gu, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 80);
}

export function dedupeSlug(slug: string, used: Set<string>): string {
    if (!slug) slug = "section";
    if (!used.has(slug)) {
        used.add(slug);
        return slug;
    }
    let n = 2;
    while (used.has(`${slug}-${n}`)) n += 1;
    const next = `${slug}-${n}`;
    used.add(next);
    return next;
}

function extractToc(doc: MinimalDoc): Heading[] {
    const nodes = doc.querySelectorAll("h2, h3");
    const used = new Set<string>();
    return nodes.map((el) => {
        const level = el.tagName === "H3" ? 3 : 2;
        const text = (el.textContent ?? "").trim();
        const slug = dedupeSlug(slugify(text), used);
        return { level: level as 2 | 3, text, slug };
    });
}

function countWords(doc: MinimalDoc): number {
    const text = (doc.body.textContent ?? "").trim();
    if (!text) return 0;
    return text.split(/\s+/).filter(Boolean).length;
}

function linkCounts(doc: MinimalDoc, siteHost: string | null): { internal: number; external: number } {
    const anchors = doc.querySelectorAll("a[href]");
    let internal = 0;
    let external = 0;
    for (const a of anchors) {
        const href = a.getAttribute("href") ?? "";
        if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) continue;
        if (href.startsWith("/")) {
            internal += 1;
            continue;
        }
        try {
            const url = new URL(href, "https://placeholder.local");
            const host = url.host.toLowerCase();
            if (siteHost && (host === siteHost || host.endsWith(`.${siteHost}`))) {
                internal += 1;
            } else {
                external += 1;
            }
        } catch {
            internal += 1;
        }
    }
    return { internal, external };
}

function imagesWithAlt(doc: MinimalDoc): { total: number; withAlt: number } {
    const imgs = doc.querySelectorAll("img");
    const total = imgs.length;
    const withAlt = imgs.filter((img) => (img.getAttribute("alt") ?? "").trim().length > 0).length;
    return { total, withAlt };
}

function matchesHeading(doc: MinimalDoc, regex: RegExp): boolean {
    const headings = doc.querySelectorAll("h1, h2, h3");
    return headings.some((h) => regex.test((h.textContent ?? "").toLowerCase()));
}

function blockquoteCount(doc: MinimalDoc): number {
    return doc.querySelectorAll("blockquote").length;
}

// Counts inline media embeds. Both <iframe> (YouTube/Vimeo, the pipeline's
// default video format from lib/youtube-placement.ts) and <video> are
// considered. Any iframe in article body HTML is overwhelmingly a video
// embed in our pipeline — we don't sanitize-allow other iframe sources.
function videoEmbedCount(doc: MinimalDoc): number {
    return doc.querySelectorAll("iframe").length + doc.querySelectorAll("video").length;
}

function hasStatisticsData(doc: MinimalDoc): boolean {
    // PDF spec (Part 2 #13, Part 3 §7): 10+ specific data points per article —
    // grams, calories, mg, percentages, dollars, etc. The previous regex
    // matched any 3+ digit run, which let years and IDs satisfy the gate.
    // We now count number+unit pairs, which is what the spec actually means.
    const text = doc.body.textContent ?? "";
    const unitPattern = /\b\d+(?:[.,]\d+)?\s*(?:%|g\b|kg\b|mg\b|mcg\b|kcal\b|cal\b|calories?\b|grams?\b|milligrams?\b|oz\b|ounces?\b|lb\b|pounds?\b|ml\b|liters?\b|tsp\b|tbsp\b|cups?\b|servings?\b|minutes?\b|hours?\b|days?\b|weeks?\b|months?\b|years?\b|x\b)/gi;
    const currencyPattern = /(?:\$|€|£|¥)\s?\d+(?:[.,]\d+)?/g;
    const total = (text.match(unitPattern) ?? []).length
                + (text.match(currencyPattern) ?? []).length;
    return total >= 10;
}

function hasJsonLdSchema(doc: MinimalDoc, hasFaq: boolean): boolean {
    if (doc.querySelector('script[type="application/ld+json"]')) return true;
    const hasH1 = doc.querySelector("h1") !== null;
    const h2s = doc.querySelectorAll("h2").length;
    return hasFaq && hasH1 && h2s >= 2;
}

function bucketFor(score: number): Bucket {
    if (score >= 80) return "green";
    if (score >= 60) return "amber";
    return "red";
}

// Derives per-check thresholds from a site fingerprint. Falls back to the
// static defaults when the fingerprint is missing or under-sampled.
//
// Multipliers below are deliberately under 1.0 — we score against a
// realistic floor (a typical article should reach 60-70% of the site's
// median pattern), not against the median itself, which would force every
// article to be average or fail. Same logic mirrors how the static
// defaults sit below the historical median.
//
// Only the three checks most volatile across niches adapt: blockquote
// floor, internal-links floor, external-links floor. Caps stay fixed —
// they exist to catch outliers (14+ external links = resource page) and
// loosening them defeats the purpose.
type AdaptiveThresholds = {
    blockquoteFloor: number;
    internalLinksFloor: number;
    externalLinksFloor: number;
    sourceLabel: "fingerprint" | "default";
};

function deriveAdaptiveThresholds(
    wordCount: number,
    fingerprint: SiteFingerprint | null | undefined,
): AdaptiveThresholds {
    const defaultBlockquoteFloor = wordCount < 1500 ? 1 : 3;

    if (!fingerprint || fingerprint.sampleSize < MIN_FINGERPRINT_SAMPLE) {
        return {
            blockquoteFloor: defaultBlockquoteFloor,
            internalLinksFloor: DEFAULT_THRESHOLDS.internalLinks,
            externalLinksFloor: DEFAULT_THRESHOLDS.externalLinks,
            sourceLabel: "default",
        };
    }

    // Each adaptive floor is N% of the site's rolling median, clamped at 1.
    // The fingerprint REPLACES the defaults — it doesn't compose with them
    // — so a site with low historical blockquote count gets a low floor
    // (respects reality) and a site with heavy citations gets a high one
    // (adapts upward). The 0.6/0.7 multipliers leave headroom: a typical
    // article should hit 60-70% of the median, not the median itself
    // (which by definition only half the corpus does).
    return {
        blockquoteFloor: Math.max(1, Math.floor(fingerprint.blockquotes * 0.6)),
        internalLinksFloor: Math.max(1, Math.floor(fingerprint.internalLinks * 0.6)),
        externalLinksFloor: Math.max(1, Math.floor(fingerprint.externalLinks * 0.7)),
        sourceLabel: "fingerprint",
    };
}

export function analyzeFromDoc(doc: MinimalDoc, meta: AnalyzeMeta): ScoreReport {
    const wordCount = countWords(doc);
    const readingMinutes = Math.max(1, Math.ceil(wordCount / 200));
    const images = doc.querySelectorAll("img").length;
    const { internal: internalLinks, external: externalLinks } = linkCounts(doc, meta.siteHost);
    const { total: imgTotal, withAlt: imgWithAlt } = imagesWithAlt(doc);

    const h2Count = doc.querySelectorAll("h2").length;
    const h3Count = doc.querySelectorAll("h3").length;
    const paragraphCount = doc.querySelectorAll("p").length;
    const bqCount = blockquoteCount(doc);
    const videoCount = videoEmbedCount(doc);
    const toc = extractToc(doc);
    const hasFaq = matchesHeading(doc, /faq|frequently asked|preguntas frecuentes/);
    const hasKeyTakeaways = matchesHeading(doc, /key takeaway|takeaways|tl;?dr|summary|resumen/);

    const keywordStr = (meta.keyword ?? "").trim();
    const metaLength = (meta.metaDescription ?? "").trim().length;
    const metaInRange = metaLength >= 110 && metaLength <= 170;

    const adaptive = deriveAdaptiveThresholds(wordCount, meta.siteFingerprint);
    const internalLinksApplicable = (meta.publishedSiblingCount ?? Infinity) >= 2;
    const internalLinksPassed = internalLinks >= adaptive.internalLinksFloor;

    // Blockquote band: floor scales with length when no fingerprint exists,
    // otherwise comes from the site's rolling median (see
    // deriveAdaptiveThresholds). Cap stays at 8 either way — too many
    // blockquotes turn the page into a pull-quote wall regardless of
    // length or site pattern.
    const blockquoteFloor = adaptive.blockquoteFloor;
    const blockquoteBandPassed = bqCount >= blockquoteFloor && bqCount <= 8;

    // H3 sub-structure check is only meaningful when (a) the article is long
    // enough to warrant breaking sections into subsections, and (b) the
    // article uses H2s as top-level sections (some templates use H3 only,
    // which the formatting gate already accepts). Otherwise mark N/A so
    // the check doesn't drag down the score for valid edge cases.
    const h3SubstructureApplicable = h2Count > 0 && wordCount >= 1500;
    const h3SubstructurePassed = h3Count >= h2Count;
    const items: Array<ChecklistItem & { weight: number; state: "passed" | "failed" | "na" }> = [
        {
            key: "structure",
            label: "Optimal content structure",
            passed: h2Count >= 3 && wordCount >= 600,
            severity: "required",
            state: h2Count >= 3 && wordCount >= 600 ? "passed" : "failed",
            weight: 10,
        },
        {
            key: "cited-sources",
            label: "Cited sources",
            passed: externalLinks >= 1,
            severity: "recommended",
            state: externalLinks >= 1 ? "passed" : "failed",
            weight: 10,
        },
        {
            key: "internal-links",
            label: internalLinksApplicable
                ? "Internal links"
                // Show progress so the user knows publishing more articles
                // is what moves the needle, instead of the static N/A text
                // that gave no signal whether they were 0% or 50% there.
                : `Internal links — N/A (${meta.publishedSiblingCount ?? 0}/2 published)`,
            passed: internalLinksPassed,
            severity: "recommended",
            state: !internalLinksApplicable ? "na" : (internalLinksPassed ? "passed" : "failed"),
            weight: 10,
        },
        {
            // Label kept short ("External links") — full diagnostic info
            // (count, threshold, fingerprint-derived flag) lives in the
            // tooltip so the panel reads cleanly at a glance.
            key: "external-links",
            label: "External links",
            passed: externalLinks >= adaptive.externalLinksFloor,
            severity: "recommended",
            state: externalLinks >= adaptive.externalLinksFloor ? "passed" : "failed",
            weight: 5,
            tooltip: adaptive.sourceLabel === "fingerprint"
                ? `${externalLinks} of ${adaptive.externalLinksFloor}+ recommended (threshold from this site's rolling median).`
                : `${externalLinks} of ${adaptive.externalLinksFloor}+ authoritative citations recommended (peer-reviewed studies, .gov/.edu, reputable publications). Soft check — articles below the floor still publish.`,
        },
        {
            // Coaching-style blockquotes, scaled to article length.
            // Replaces the older binary "any blockquote" check, which
            // accepted lone quotes that didn't add coaching value and
            // treated articles with 12 quotes the same as articles with 3.
            // The band rewards presence and restraint — too many blockquotes
            // turn the page into a pull-quote wall.
            key: "coaching-blockquotes",
            label: "Blockquotes",
            passed: blockquoteBandPassed,
            severity: "recommended",
            state: blockquoteBandPassed ? "passed" : "failed",
            weight: 5,
            tooltip: adaptive.sourceLabel === "fingerprint"
                ? `${bqCount} blockquote(s) — ${blockquoteFloor}-8 ideal (floor from this site's median). Caps at 8 to prevent pull-quote walls.`
                : `${bqCount} blockquote(s) — ${blockquoteFloor}-8 ideal. Below the floor the coaching voice is missing; above 8 turns the article into a wall of pull-quotes.`,
        },
        {
            // H3 sub-structure: every H2 should typically break down into
            // 2-3 H3 subsections for scannability and Google passage indexing.
            // N/A for short articles where flat structure is genuinely
            // appropriate, and N/A when the article uses H3 as top-level
            // (the formatting gate accepts that template variant).
            key: "h3-substructure",
            label: "H3 sub-structure",
            passed: !h3SubstructureApplicable || h3SubstructurePassed,
            severity: "recommended",
            state: !h3SubstructureApplicable ? "na" : (h3SubstructurePassed ? "passed" : "failed"),
            weight: 5,
            tooltip: !h3SubstructureApplicable
                ? wordCount < 1500
                    ? "Skipped — short articles don't need H3 sub-structure."
                    : "Skipped — article uses no H2 headings."
                : `${h3Count} H3 across ${h2Count} H2. Each H2 should typically break into 2-3 H3 subsections.`,
        },
        {
            // External-links cap. The existing external-links floor rewards
            // 8+ authoritative citations; this check catches the opposite
            // failure mode — articles with 13+ outbound links read as
            // curated resource pages, dilute link equity, and erode the
            // perception of original analysis. 12 is a soft ceiling.
            key: "external-links-cap",
            label: "External links cap",
            passed: externalLinks <= 12,
            severity: "recommended",
            state: externalLinks <= 12 ? "passed" : "failed",
            weight: 5,
            tooltip: `${externalLinks} of 12 max. More than ~12 outbound links makes an article look like a resource page rather than original analysis.`,
        },
        {
            key: "statistics",
            label: "Statistics data points",
            passed: hasStatisticsData(doc),
            severity: "recommended",
            state: hasStatisticsData(doc) ? "passed" : "failed",
            weight: 5,
        },
        {
            // PDF Part 5 target: 60+ paragraphs per article (vs RankSector's 1-4
            // monolithic baseline). Threshold of 30 keeps the hard-min 1500-word
            // article achievable (1500w / 50w avg per paragraph = 30) while
            // still flagging the wall-of-text failure mode.
            key: "paragraph-count",
            label: "Paragraph count",
            passed: paragraphCount >= 30,
            severity: "recommended",
            state: paragraphCount >= 30 ? "passed" : "failed",
            weight: 5,
        },
        {
            // Required: the pre-QC `ensureInlineImages` step (autopilot.ts)
            // injects an Unsplash inline image with non-empty alt before this
            // gate runs, so missing images here means BOTH the LLM didn't
            // produce them AND the Unsplash fallback failed — which we want
            // to block on, not silently publish.
            key: "alt-texts",
            label: "Image alt texts",
            passed: imgTotal === 0 ? false : imgWithAlt === imgTotal,
            severity: "required",
            state: imgTotal === 0 ? "failed" : (imgWithAlt === imgTotal ? "passed" : "failed"),
            weight: 10,
        },
        {
            key: "faq",
            label: hasFaq ? "FAQ section" : "FAQ section — N/A (optional per PDF spec)",
            passed: hasFaq,
            severity: "recommended",
            state: hasFaq ? "passed" : "na",
            weight: 10,
        },
        {
            key: "meta-tags",
            label: "Optimized meta tags",
            passed: metaInRange,
            severity: "required",
            state: metaInRange ? "passed" : "failed",
            weight: 15,
        },
        {
            key: "json-ld",
            label: "JSON-LD schema",
            passed: hasJsonLdSchema(doc, hasFaq),
            severity: "recommended",
            state: hasJsonLdSchema(doc, hasFaq) ? "passed" : "failed",
            weight: 10,
        },
    ];

    const scorableItems = items.filter((it) => it.state !== "na");
    const totalWeight = scorableItems.reduce((sum, it) => sum + it.weight, 0);
    const passedWeight = scorableItems
        .filter((it) => it.passed)
        .reduce((sum, it) => sum + it.weight, 0);
    const rawScore = totalWeight > 0 ? Math.round((passedWeight / totalWeight) * 100) : 0;
    const score = Math.min(100, Math.max(0, rawScore));

    return {
        score,
        bucket: bucketFor(score),
        stats: {
            wordCount,
            readingMinutes,
            keywords: keywordStr.length > 0 ? 1 : 0,
            images,
            internalLinks,
            externalLinks,
            h2Count,
            h3Count,
            blockquotes: bqCount,
            paragraphs: paragraphCount,
            videoEmbeds: videoCount,
        },
        checklist: items.map((it) => ({
            key: it.key,
            label: it.label,
            passed: it.passed,
            severity: it.severity,
            state: it.state,
            // Tooltip text was being stripped here, leaving every checklist
            // row's hover tooltip empty in the dashboard. Surface it now —
            // the property already exists in ChecklistItem.
            tooltip: it.tooltip,
        })),
        toc,
        hasFaq,
        hasKeyTakeaways,
    };
}
